import * as Sentry from '@sentry/nextjs';

/**
 * Interface for user data that can be sent to Sentry
 */
interface SentryUser {
    id?: string;
    email?: string;
    username?: string;
    role?: string;
    [key: string]: unknown; // Allow additional arbitrary properties
}

/**
 * Set user context for Sentry error tracking.
 * This function can be called manually to update user context.
 *
 * @param user - User object or null to clear user context
 *
 * @example
 * ```typescript
 * // Set user context
 * setSentryUser({
 *   id: 'user-123',
 *   email: 'user@example.com',
 *   username: 'john_doe',
 *   role: 'admin'
 * });
 *
 * // Clear user context
 * setSentryUser(null);
 * ```
 */
export function setSentryUser(user: SentryUser | null): void {
    Sentry.setUser(user);
}

/**
 * Add additional context to the current Sentry user.
 * This is useful for adding temporary context without overriding the entire user object.
 *
 * @param context - Additional context to add
 *
 * @example
 * ```typescript
 * setSentryUserContext({
 *   current_project: 'project-456',
 *   subscription_plan: 'premium'
 * });
 * ```
 */
export function setSentryUserContext(context: Record<string, unknown>): void {
    Sentry.setContext('user_context', context);
}

/**
 * Set user tags for Sentry. Tags are searchable and can be used for filtering.
 *
 * @param tags - Tags to set
 *
 * @example
 * ```typescript
 * setSentryUserTags({
 *   user_type: 'premium',
 *   signup_source: 'google'
 * });
 * ```
 */
export function setSentryUserTags(tags: Record<string, string>): void {
    Sentry.setTags(tags);
}

/**
 * Clear all user context from Sentry
 */
export function clearSentryUser(): void {
    Sentry.setUser(null);
}
